import { PRODUCT_BRANDS } from 'types/products';
import { IAdditionalSettings } from '../components/PendingSettingsIndicator/PendingSettingsIndicator';
import {
  IDestination,
  IorderProductsData,
  IproductReferenceDataInCountry,
  IreferencesByCountry
} from '../types/orders';
import { LOCATION_PLACE_TYPE } from '../types/services';
import { CONFIG_STATUS_ID } from '../types/product.configuration.types';
import { checkAllActionsConfirmed } from './configurations';
import { formatRoundedUpNumber } from './formatNumbers';
import { CURRENCIES } from 'types/bussines';

const getBringTotalAdditionalSettings = (
  sku?: string,
  shipmentGroupId?: number,
  references?: IproductReferenceDataInCountry[],
  productIds?: number[]
): IAdditionalSettings => {
  const defaultValues = {
    total: 0,
    toBeCompleted: 0,
    completed: 0,
    notCompleted: 0,
    withUnconfirmedActions: 0,
    remainingMPNs: 0
  };

  if (!sku || !shipmentGroupId) return defaultValues;

  const reference = references?.find(reference => reference?.sku === sku);

  const productIdSet = new Set(productIds);

  const items = reference?.items?.filter(item => item.productId != null && productIdSet.has(item.productId as number));

  if (!items || items.length === 0) return defaultValues;

  const totalConfigurations = Math.round(
    items.reduce((total, item) => total + (item.configurations?.length || 0), 0)
  );

  const toBeCompleted = Math.round(
    items.reduce((total, item) => {
      return (
        total +
        (item.configurations?.filter(config => config.status?.statusId === CONFIG_STATUS_ID.TO_COMPLETE)?.length || 0)
      );
    }, 0)
  );

  const completed = Math.round(
    items.reduce((total, item) => {
      return (
        total +
        (item.configurations?.filter(config => {
          return (
            config.status?.statusId === CONFIG_STATUS_ID.COMPLETED &&
            checkAllActionsConfirmed(config?.additionalActions)
          );
        })?.length || 0)
      );
    }, 0)
  );

  const notCompleted = Math.round(
    items.reduce((total, item) => {
      return (
        total +
        (item.configurations?.filter(config => config.status?.statusId === CONFIG_STATUS_ID.NOT_COMPLETED)?.length || 0)
      );
    }, 0)
  );

  const withUnconfirmedActions = Math.round(
    items.reduce((total, item) => {
      return (
        total +
        (item.configurations?.filter(config => !checkAllActionsConfirmed(config?.additionalActions))?.length || 0)
      );
    }, 0)
  );

  const remainingMPNs = Math.round(
    items.reduce((total, item) => {
      return total + (!item.mpnId && reference?.brand?.toLowerCase() === PRODUCT_BRANDS.APPLE.toLowerCase() ? 1 : 0);
    }, 0)
  );

  return { total: totalConfigurations, toBeCompleted, completed, notCompleted, withUnconfirmedActions, remainingMPNs };
};

const getShipmentDestinationText = (destination?: IDestination | null): string | null => {
  if (!destination) return null;
  const { place, receiverName, countryName } = destination;
  if (place === LOCATION_PLACE_TYPE.USER) {
    return receiverName || null;
  }
  if (place === LOCATION_PLACE_TYPE.OFFICE) {
    return countryName ? `Oficina (${countryName})` : null;
  }
  if (place === LOCATION_PLACE_TYPE.NUDOS) {
    return countryName ? `Bodega (${countryName})` : null;
  }
  return null;
};

const normalizeSaleId = (id: string | number | null | undefined): number | null => {
  if (id === null || id === undefined) return null;
  const numId = typeof id === 'number' ? id : Number(id);
  return isNaN(numId) ? null : numId;
};

const findSkuBySaleId = (saleId: number, productsByReferenceList: IorderProductsData): string | null => {
  const allReferences = Object.values(productsByReferenceList).flatMap(
    countryData => (countryData as IreferencesByCountry)?.references || []
  );

  const referenceWithSaleId = allReferences.find(reference => {
    const items = reference?.items || [];
    return items?.some(item => {
      const itemSaleId = normalizeSaleId(item?.saleId);
      return itemSaleId !== null && itemSaleId === saleId;
    });
  });

  return referenceWithSaleId?.sku || null;
};

const formatOrderAmount = (total?: string | null, totalUsd?: string | null, currency?: string | null): string => {
  const amount = [CURRENCIES.USD, CURRENCIES.ARS, CURRENCIES.BRL].includes(currency as CURRENCIES)
    ? formatRoundedUpNumber(Number(totalUsd || 0), 2)
    : formatRoundedUpNumber(Number(total || 0), 2);
  const currencyDisplay = [CURRENCIES.BRL, CURRENCIES.ARS].includes(currency as CURRENCIES)
    ? CURRENCIES.USD
    : currency || CURRENCIES.USD;
  return `$${amount}${currencyDisplay}`;
};

export {
  getBringTotalAdditionalSettings,
  getShipmentDestinationText,
  findSkuBySaleId,
  normalizeSaleId,
  formatOrderAmount
};
